/**
 * Pure, deterministic content-draft generators for the Content Engine.
 *
 * WHY this module exists: the generators used to live inline in
 * `ContentEngineView.tsx`, where they couldn't be exercised headlessly (a .tsx
 * view needs a DOM to mount). Extracting them here keeps the view purely
 * presentational and makes the personalization logic — the part that actually
 * earns its keep — unit-testable (see `drafts.test.ts`). No React, no network,
 * no store: given a sender name + optional persona/target records, each returns
 * a plain string.
 *
 * Honesty: these are template-driven, NOT generative. The view's "Deterministic
 * drafts" pill stays accurate until the Claude worker is wired
 * (`VITE_AGENT_API_URL`); this module never pretends to be live Claude.
 *
 * Note on the `target` param: it's typed as `Person` and named generically
 * because a draft can be aimed at a prospect OR an existing customer — both are
 * `Person` records carrying `name`/`company`/`title`/`lastTouch`, so the
 * templates don't care which category they came from.
 */

import type { Person } from '../db/types';

/** The four asset types the Content Engine can compose. */
export type AssetType = 'linkedin' | 'cold-email' | 'follow-up-email' | 'one-pager';

/** Generate a LinkedIn post draft. */
export function draftLinkedIn(persona?: Person, target?: Person): string {
  const role = persona?.profile?.role || target?.title || 'leader';
  const painPoints = persona?.profile?.painPoints || [];
  const goals = persona?.profile?.goals || [];

  const hook =
    painPoints.length > 0
      ? `The most common problem I hear from ${role}s: ${painPoints[0]}`
      : `Most ${role}s are operating without visibility.`;

  const para1 = goals.length > 0
    ? `Here's why: ${goals[0]} stays at the top of the list, but execution usually doesn't keep up.`
    : `The symptoms are predictable. Tickets slip. Knowledge lives in heads. Every manual win is fragile.`;

  const para2 = `Argo IQ fixes that. We deploy an AI operating layer—agents, an AQI scoring engine, and managed delivery—that turns the work you're already doing into a system.`;

  const para3 = `If this resonates, let's talk. There's usually a 90-day window to prove it.`;

  return [
    hook,
    '',
    para1,
    '',
    para2,
    '',
    para3,
    '',
    '#ArgoIQ #OperatingLayers #AI',
  ].join('\n');
}

/**
 * Generate a cold email draft.
 * `sender` is the signed-in operator's first name so the draft signs honestly
 * as whoever is actually composing it — never a hardcoded teammate.
 */
export function draftColdEmail(sender: string, persona?: Person, target?: Person): string {
  const targetName = target?.name || 'there';
  const targetCompany = target?.company || 'your company';
  const targetTitle = target?.title || 'leader';
  const painPoint = persona?.profile?.painPoints?.[0] || 'execution at scale';
  const goal = persona?.profile?.goals?.[0] || 'velocity';

  const subject = `Helping ${targetTitle}s solve ${painPoint}`;

  const body = [
    `Hi ${targetName},`,
    '',
    `I work with ${targetTitle}s at companies like ${targetCompany} who are frustrated that ${painPoint} slows them down.`,
    '',
    `The bottleneck is rarely strategy. It's usually systems—${goal} gets gated by the people who remember.`,
    '',
    `We run an AI operating layer (Argo IQ) that handles the work you shouldn't have to. Agents, visibility, and an AQI score to measure the gain.`,
    '',
    `Most customers see movement in 90 days. Worth a quick call?`,
    '',
    sender,
  ].join('\n');

  return `Subject: ${subject}\n\n${body}`;
}

/**
 * Generate a follow-up email draft.
 * `sender` is the signed-in operator's first name (see draftColdEmail).
 */
export function draftFollowUp(sender: string, persona?: Person, target?: Person): string {
  const targetName = target?.name || 'there';
  const lastTouch = target?.lastTouch
    ? new Date(target.lastTouch).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : 'last week';

  const goal = persona?.profile?.goals?.[0] || 'velocity';
  const kpi = persona?.profile?.kpis?.[0] || 'throughput';

  return [
    `Hi ${targetName},`,
    '',
    `Following up on our conversation ${lastTouch} about ${goal}.`,
    '',
    `I was thinking about what you said re: ${kpi}. Here's the thing: it's almost always a systems problem, not a people problem. You have the right people. You just need the systems to keep up.`,
    '',
    `That's why Argo IQ exists. We take the manual overhead off your team and move it onto agents + an AI operating layer.`,
    '',
    `Interested in 20 minutes next week to walk through how we'd approach your situation?`,
    '',
    sender,
  ].join('\n');
}

/** Generate a one-pager intro draft. */
export function draftOnePager(persona?: Person, target?: Person): string {
  const company = target?.company || persona?.company || 'Company';
  const painPoint =
    persona?.profile?.painPoints?.[0] ||
    'execution at scale';
  const goal = persona?.profile?.goals?.[0] || 'velocity';
  const kpi = persona?.profile?.kpis?.[0] || 'throughput';

  return [
    '# The Argo IQ Operating Layer',
    '',
    `## Problem: ${company} is outpacing its systems`,
    '',
    `Your team is sharp. The strategy is sound. But ${painPoint} keeps gating ${goal}—because too much work still depends on memory instead of systems.`,
    '',
    '## Promise: Move the work onto agents',
    '',
    `Argo IQ deploys an AI operating layer—agents that handle tickets, research, and outreach; an AQI engine that measures ${kpi}; and managed delivery so you don't have to maintain it.`,
    '',
    `Result: Your ${goal} compounds every sprint instead of staying flat.`,
    '',
    '## Proof',
    '',
    '- Customer A saw a 40% reduction in ticket-to-close cycle',
    '- Customer B freed 15 hours/week for high-touch work',
    '- Customer C scaled from 3 to 8 concurrent engagements without headcount',
    '',
    `Ready? Let's scope it. 90-day guaranteed improvement in ${kpi} or we keep working.`,
  ].join('\n');
}

/**
 * Single dispatch entry: pick a generator by asset type. Keeps the view's
 * effect a one-liner and gives tests one stable surface to exercise every type.
 * `sender` is only consumed by the email templates; the others ignore it.
 */
export function generateDraft(
  assetType: AssetType,
  sender: string,
  persona?: Person,
  target?: Person,
): string {
  switch (assetType) {
    case 'linkedin':
      return draftLinkedIn(persona, target);
    case 'cold-email':
      return draftColdEmail(sender, persona, target);
    case 'follow-up-email':
      return draftFollowUp(sender, persona, target);
    case 'one-pager':
      return draftOnePager(persona, target);
  }
}
